import { TabbedIncidentsTable } from "fleet/view/incidents/tabbed-incidents-table";
import PropTypes from "prop-types";
import React from "react";
import * as Users from "redux-store/users/selectors";
import {
  DATE_PRESET_LAST_1_HOUR,
  DATE_PRESET_LAST_24_HOURS,
  DATE_PRESET_LAST_7_DAYS,
  DATE_PRESET_LAST_30_DAYS,
  FILTER_TYPE_DATE_RANGE,
  FILTER_TYPE_MULTI_SELECT,
  FILTER_TYPE_SELECT,
  Filter,
} from "ui/filter";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { sortByLabelAsc } from "util/sort-by-prop";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

class IncidentsList extends React.PureComponent {
  static get propTypes() {
    return {
      incidents: PropTypes.array.isRequired,
      loadIncidents: PropTypes.func.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      unloadIncidents: PropTypes.func.isRequired,
      users: PropTypes.object.isRequired,
    };
  }

  constructor(props) {
    super(props);

    this.filterApi = null;

    this._getAlertNameOptions = this._getOptions.bind(null, "name");
    this._getDeviceNameOptions = this._getOptions.bind(null, "device_name");

    this._getSelectOptions = this._getSelectOptions.bind(this);
    this._onFilterChange = this._onFilterChange.bind(this);
    this._shouldUpdateSelectOptions =
      this._shouldUpdateSelectOptions.bind(this);
    this.getFilterApi = this.getFilterApi.bind(this);
    this.resolveMsgHandler = this.resolveMsgHandler.bind(this);

    this.state = {
      errorMessage: "",
      successMessage: "",
      tableData: props.incidents,
      ...this._getSelectOptions(props),
    };
  }

  componentDidMount() {
    this.props.loadIncidents();
  }

  componentDidUpdate(prevProps) {
    const { incidents, storesLoaded } = prevProps;

    if (this._shouldUpdateSelectOptions(incidents, storesLoaded)) {
      const selectOptions = this._getSelectOptions(this.props);
      this.setState(selectOptions);
    }
  }

  componentWillUnmount() {
    this.props.unloadIncidents();
  }

  resolveMsgHandler(message) {
    this.setState(message);
  }

  _shouldUpdateSelectOptions(incidents, storesLoaded) {
    return (
      incidents !== this.props.incidents ||
      storesLoaded !== this.props.storesLoaded
    );
  }

  _getFilterFields() {
    const { alertNameOptions, deviceNameOptions, userOptions } = this.state;

    return [
      {
        label: "Alert Name",
        name: "name",
        placeholder: "Alert Name",
        showPlaceholder: true,
        type: FILTER_TYPE_SELECT,
        options: alertNameOptions,
      },
      {
        label: "Device Name",
        name: "device_name",
        placeholder: "Device Name",
        showPlaceholder: true,
        type: FILTER_TYPE_SELECT,
        options: deviceNameOptions,
      },
      {
        label: "First Occurrence Date Range",
        name: "create_date",
        type: FILTER_TYPE_DATE_RANGE,
        placeholder: {
          startDate: "Start Date",
          endDate: "End Date",
        },
        options: [
          DATE_PRESET_LAST_1_HOUR,
          DATE_PRESET_LAST_24_HOURS,
          DATE_PRESET_LAST_7_DAYS,
          DATE_PRESET_LAST_30_DAYS,
        ],
      },
      {
        label: "Last Notified Date Range",
        name: "last_notified",
        type: FILTER_TYPE_DATE_RANGE,
        placeholder: {
          startDate: "Start Date",
          endDate: "End Date",
        },
        options: [
          DATE_PRESET_LAST_1_HOUR,
          DATE_PRESET_LAST_24_HOURS,
          DATE_PRESET_LAST_7_DAYS,
          DATE_PRESET_LAST_30_DAYS,
        ],
      },
      {
        label: "Notified",
        name: "notified",
        placeholder: "Notified Users",
        showPlaceholder: true,
        type: FILTER_TYPE_MULTI_SELECT,
        options: userOptions,
      },
    ];
  }

  _getUserOptions(incidents, users) {
    const set = new Set();
    const userOptions = [];

    incidents.forEach((incident) => {
      const notifiedUsers = incident.notified;

      notifiedUsers.forEach((userId) => {
        if (set.has(userId)) {
          return;
        }

        set.add(userId);

        const user = users[userId];

        userOptions.push({
          label: Users.toString(user, null),
          value: String(userId),
        });
      });
    });

    return userOptions.sort(sortByLabelAsc);
  }

  _getOptions(property, incidents) {
    const set = new Set();
    return incidents
      .map((incident) => {
        const value = incident[property];

        if (set.has(value)) {
          return null;
        }

        set.add(value);

        return {
          label: value,
          value: value,
        };
      })
      .filter((opt) => opt)
      .sort(sortByLabelAsc);
  }

  _onFilterChange() {
    this.forceUpdate();
  }

  _getSelectOptions(props) {
    const { incidents, users } = props;

    return {
      alertNameOptions: this._getAlertNameOptions(incidents),
      deviceNameOptions: this._getDeviceNameOptions(incidents),
      userOptions: this._getUserOptions(incidents, users),
    };
  }

  getFilterApi(filterApi) {
    this.filterApi = filterApi;
  }

  renderView() {
    const filterFields = this._getFilterFields();
    const { incidents, storesLoaded } = this.props;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    const tableData = this.filterApi
      ? this.filterApi.getFilteredData(incidents)
      : incidents;

    return (
      <div>
        <Filter
          onFilterChange={this._onFilterChange}
          getApi={this.getFilterApi}
          fields={filterFields}
        />
        <TabbedIncidentsTable
          tableData={tableData}
          resolveMsgHandler={this.resolveMsgHandler}
          noHeader={true}
          showDeviceNames={true}
          sizePerPage={25}
        />
      </div>
    );
  }

  render() {
    const { errorMessage, successMessage } = this.state;
    const view = this.renderView();

    return (
      <Page title="Triggered Alerts">
        <Notification error={errorMessage} success={successMessage} />
        <Box noHeader={true}>{view}</Box>
      </Page>
    );
  }
}

export { IncidentsList };
